/**
 * Mode dégradé (CP-12) — où dort le coffre sur le poste : IndexedDB, un
 * enregistrement par agent qui s'est connecté ici. Rien n'y est en clair sauf
 * de quoi proposer le bon coffre (une adresse, un nom, un site, des dates).
 *
 * Volontairement mince : pas de bibliothèque, et toute erreur de stockage
 * (navigation privée, quota, poste verrouillé) se traduit par « pas de
 * coffre » — le mode dégradé aide, il ne casse jamais la connexion.
 */
import { coffreTropVieux } from '@afs/shared'
import type { Coffre } from './coffre.js'

const BASE = 'afs-hors-ligne'
const MAGASIN = 'coffres'

export interface CoffreRange {
  /** Adresse de connexion, en minuscules : la clé de l'enregistrement. */
  readonly email: string
  readonly nomAffiche: string
  readonly siteNom: string | null
  /** L'instant où le serveur a calculé les verdicts : « Données du 12/03 à 08:15 ». */
  readonly genereLe: string
  readonly scelleLe: string
  readonly nbPersonnes: number
  readonly coffre: Coffre
}

function ouvrirBase(): Promise<IDBDatabase> {
  return new Promise((resoudre, rejeter) => {
    const demande = indexedDB.open(BASE, 1)
    demande.onupgradeneeded = () => {
      demande.result.createObjectStore(MAGASIN, { keyPath: 'email' })
    }
    demande.onsuccess = () => {
      resoudre(demande.result)
    }
    demande.onerror = () => {
      rejeter(demande.error ?? new Error('IndexedDB indisponible'))
    }
  })
}

/** IndexedDB rend ce qu'on y a rangé, sans type : le résultat est `unknown`, à qui appelle de le lire. */
async function transaction(
  mode: IDBTransactionMode,
  action: (magasin: IDBObjectStore) => IDBRequest,
): Promise<unknown> {
  const base = await ouvrirBase()
  try {
    return await new Promise<unknown>((resoudre, rejeter) => {
      const demande = action(base.transaction(MAGASIN, mode).objectStore(MAGASIN))
      demande.onsuccess = () => {
        resoudre(demande.result as unknown)
      }
      demande.onerror = () => {
        rejeter(demande.error ?? new Error('Lecture du coffre impossible'))
      }
    })
  } finally {
    base.close()
  }
}

export const cleDe = (email: string): string => email.trim().toLowerCase()

export async function lireCoffre(email: string): Promise<CoffreRange | null> {
  try {
    // Seul ce module écrit dans ce magasin : ce qu'on y lit est ce qu'il y a rangé.
    const range = await transaction('readonly', (m) => m.get(cleDe(email)))
    return (range as CoffreRange | undefined) ?? null
  } catch {
    return null
  }
}

export async function rangerCoffre(range: CoffreRange): Promise<boolean> {
  try {
    await transaction('readwrite', (m) => m.put({ ...range, email: cleDe(range.email) }))
    return true
  } catch {
    return false
  }
}

/** Les coffres du poste, sans leur contenu — pour dire à l'écran de quand datent les données. */
export async function listerCoffres(): Promise<Omit<CoffreRange, 'coffre'>[]> {
  try {
    const tous = (await transaction('readonly', (m) => m.getAll())) as CoffreRange[]
    return tous.map((range) => ({
      email: range.email,
      nomAffiche: range.nomAffiche,
      siteNom: range.siteNom,
      genereLe: range.genereLe,
      scelleLe: range.scelleLe,
      nbPersonnes: range.nbPersonnes,
    }))
  } catch {
    return []
  }
}

/**
 * On ne garde pas indéfiniment vingt mille noms, même chiffrés, sur un poste :
 * à l'ouverture de la coque, ET à celle de l'accueil hors ligne — sans réseau,
 * personne ne se reconnecte pour purger (S74, D5).
 */
export async function purgerLesVieuxCoffres(
  maintenant = new Date(),
  /** Coffres reconnus trop vieux à leur date chiffrée, quoi que dise `scelleLe` (S77, S3). */
  aussi: readonly string[] = [],
): Promise<void> {
  const autres = new Set(aussi.map(cleDe))
  for (const range of await listerCoffres()) {
    if (autres.has(range.email) || coffreTropVieux(new Date(range.scelleLe), maintenant)) {
      await transaction('readwrite', (m) => m.delete(range.email)).catch(() => undefined)
    }
  }
}
